import { isEmpty } from "lodash";

import ApiClientAsync from "../../api/ApiClientAsync";
import config from "../../config";
import { getDefaultMarketId } from "../getDefaultMarketId";
import type {
  FinancialSituation,
  MediaMonitorData,
  MediaMonitorEntry,
  PriceAlert,
  StockPageData,
  StockPayload,
  StockRecommendation,
  StockRecommendationsPayload,
  StockTag
} from "./types";

type ApiClient = InstanceType<typeof ApiClientAsync>;

export function resolveMarketId(
  marketTranslations: Record<string, { pl?: string; en?: string }> | null | undefined,
  market: string
): string {
  if (!marketTranslations || isEmpty(marketTranslations)) {
    return market;
  }

  return getDefaultMarketId(marketTranslations, market);
}

export async function fetchStockPayload(
  client: ApiClient,
  market: string,
  ticker: string
): Promise<StockPayload> {
  const data = (await client.get({
    urlPath: "app.stocks.stock",
    variables: { marketId: market, stockId: ticker },
    skipAccessCheck: true
  })) as StockPayload;

  if (!data?.stock) {
    throw new Error("Stock not found");
  }

  return data;
}

export async function fetchStockPageData(
  client: ApiClient,
  market: string,
  ticker: string
): Promise<StockPageData> {
  return (await client.get({
    urlPath: "app.stocks.singleStock",
    variables: { marketId: market, stockId: ticker },
    skipAccessCheck: true
  })) as StockPageData;
}

export async function fetchFinancialSituation(
  client: ApiClient,
  stockId: number
): Promise<FinancialSituation> {
  return (await client.get({
    urlPath: "app.financialSituation.stock",
    variables: { stockId },
    skipAccessCheck: true
  })) as FinancialSituation;
}

type PaginatedMediaMonitor = {
  results: MediaMonitorEntry[];
  next: string | null;
};

export function normalizeMediaMonitorPage(
  data: PaginatedMediaMonitor | null | undefined
): MediaMonitorData {
  return {
    messages: data?.results || [],
    is_last_page: !data?.next
  };
}

export async function fetchMediaMonitorPage(
  client: ApiClient,
  market: string,
  ticker: string,
  start = 0,
  count = config.recommendationsToFetch
): Promise<MediaMonitorData> {
  const data = (await client.get({
    urlPath: "app.stocks.mediaMonitor",
    variables: {
      market,
      ticker,
      count,
      start
    }
  })) as PaginatedMediaMonitor;

  return normalizeMediaMonitorPage(data);
}

export async function fetchPriceAlerts(
  client: ApiClient,
  stockId: number
): Promise<PriceAlert[]> {
  const data = (await client.get({
    urlPath: "app.priceAlerts.get",
    variables: { stockId },
    skipAccessCheck: true
  })) as { alerts: PriceAlert[] };

  return data.alerts || [];
}

export async function fetchStockTags(
  client: ApiClient,
  stockId: number
): Promise<StockTag[]> {
  const data = (await client.get({
    urlPath: "app.tags.stockTags",
    skipAccessCheck: true,
    variables: { stockId }
  })) as StockTag[];

  return Array.isArray(data) ? data : [];
}

export async function fetchRecommendations(
  client: ApiClient,
  stockId: number,
  start = 0,
  count = config.recommendationsToFetch
): Promise<StockRecommendationsPayload> {
  const data = (await client.get({
    urlPath: "app.stocks.recommendations",
    variables: { stockId, count, start }
  })) as StockRecommendationsPayload;

  return {
    recommendations: data.recommendations || [],
    more_recommendations: Boolean(data.more_recommendations)
  };
}
